/**
 * Seitenleiste des Uebertragungsprotokolls (D-01, D-22; Sketch 025 `openDrawer`/CSS "Drawer"/
 * "Nutzlast-Tabelle"): Fakten, Antwort des Portals, Aenderungshinweis und die gesendeten Zeilen als
 * Beleg (`getEntry`), dazu CSV-Download aus demselben Beleg und der Rechte-abhaengige Sprung "Zur
 * Auswertung" (`canOpenEvaluation`, D-22). Alle Texte kommen fertig vom Server -- keine eigene
 * Formatierung oder Ableitung im Frontend (T-41-29: nur als React-Text gerendert, kein
 * unsicheres HTML-Einschleusen).
 *
 * Die Aktionsknoepfe bleiben sichtbar, wenn die Zeilentabelle scrollt: `Drawer.Content` ist ueber
 * `styles.content` als Flex-Spalte ueberschrieben (statt des Standard-`overflow-y: auto` auf dem
 * gesamten Inhalt), `Drawer.Body` scrollt als einziges flexibles Kind, die Fusszeile ist ein
 * dritter, nicht wachsender Flex-Bruder -- kein `position: sticky` (CLAUDE.md). Nicht `style`/
 * `className` verwenden: Mantine reicht beide auch an `Drawer.inner` weiter, die Flex-Spalte kippt
 * dort die Ausrichtung und die Leiste landet unten links statt rechts.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Drawer, Skeleton, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { statistikPortalTransferApi } from '../../services/statistikPortalTransferApi';
import { evaluationPathFor } from '../../pages/transfers/transferOverviewUrl';
import { downloadBlob } from '../../utils/downloadBlob';
import { getApiErrorMessage } from '../../utils/errorHandling';
import { TransferStatus } from './TransferStatus';
import type { TransferDetail } from '../../types/statistikPortalTransfer';
import classes from './TransferList.module.css';

export interface TransferDetailDrawerProps {
  entryId: number | null;
  onClose: () => void;
}

/** Reine Ausrichtung, keine Umrechnung: eine Spalte ist "numerisch", wenn ihr erster Wert so aussieht. */
function isNumericLooking(value: string): boolean {
  return value.length > 0 && /^-?[\d.,]+$/.test(value);
}

function numericColumns(detail: TransferDetail): boolean[] {
  const firstRow = detail.rows[0];
  if (!firstRow) {
    return detail.columns.map(() => false);
  }
  return firstRow.map((value) => isNumericLooking(value));
}

const DRAWER_CONTENT_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
} as const;

const DRAWER_BODY_STYLE = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
} as const;

export function TransferDetailDrawer({ entryId, onClose }: TransferDetailDrawerProps) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<TransferDetail | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);

  useEffect(() => {
    setDetail(null);
    setLoadFailed(false);
    if (entryId === null) {
      return;
    }

    let cancelled = false;
    statistikPortalTransferApi
      .getEntry(entryId)
      .then((result) => {
        if (!cancelled) {
          setDetail(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [entryId]);

  const handleCsvDownload = async () => {
    if (!detail) {
      return;
    }
    setCsvLoading(true);
    try {
      const { blob, filename } = await statistikPortalTransferApi.downloadEntryCsv(detail.id);
      downloadBlob(blob, filename);
      notifications.show({
        message: `${filename} heruntergeladen – die Zeilen dieser Übertragung`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Nicht heruntergeladen',
        message: getApiErrorMessage(err, 'Der CSV-Download ist fehlgeschlagen.'),
      });
    } finally {
      setCsvLoading(false);
    }
  };

  const handleOpenEvaluation = () => {
    if (!detail) {
      return;
    }
    navigate(evaluationPathFor(detail.evaluationType, detail.year, detail.period));
  };

  const numCols = detail ? numericColumns(detail) : [];

  return (
    <Drawer.Root opened={entryId !== null} onClose={onClose} position="right" size="min(640px, 100%)">
      <Drawer.Overlay />
      <Drawer.Content styles={{ content: DRAWER_CONTENT_STYLE }}>
        <Drawer.Header>
          <div className={classes.drawerHeaderInfo}>
            <Drawer.Title>
              {detail ? `${detail.evaluationLabel} · ${detail.periodLabel}` : 'Wird geladen …'}
            </Drawer.Title>
            {detail && (
              <TransferStatus
                status={detail.status}
                statusLabel={detail.statusLabel}
                changedSince={detail.changedSince}
              />
            )}
          </div>
          <Drawer.CloseButton aria-label="Schließen" />
        </Drawer.Header>
        <Drawer.Body style={DRAWER_BODY_STYLE}>
          {!detail && !loadFailed && <Skeleton height={200} />}
          {loadFailed && (
            <Alert color="red" title="Nicht geladen">
              Der Eintrag konnte nicht geladen werden.
            </Alert>
          )}
          {detail && (
            <>
              <dl className={classes.facts}>
                <dt>Übertragen von</dt>
                <dd>{detail.personLabel}</dd>
                <dt>Zeitpunkt</dt>
                <dd>
                  {detail.dateLabel}, {detail.timeLabel}
                </dd>
                <dt>Bereich</dt>
                <dd>{detail.areaLabel}</dd>
                <dt>Zieltabelle</dt>
                <dd>{detail.classname}</dd>
                <dt>Zeilen</dt>
                <dd>{detail.rowCount}</dd>
              </dl>

              {detail.status === 'transferred' ? (
                <div className={classes.responseBox}>{detail.responseSummary}</div>
              ) : (
                <div className={classes.responseBoxFail}>
                  <Text component="span" fw={700}>
                    Nicht übertragen.
                  </Text>{' '}
                  {detail.errorText}
                </div>
              )}

              {detail.changedNotice && (
                <div className={classes.changedNoticeBox}>{detail.changedNotice}</div>
              )}

              <Text fw={700} mb="xs">
                {detail.linesHeading}
              </Text>
              <div className={classes.payWrap}>
                <Table className={classes.pay}>
                  <Table.Thead>
                    <Table.Tr>
                      {detail.columns.map((column, index) => (
                        <Table.Th key={column} className={numCols[index] ? classes.payColNum : undefined}>
                          {column}
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {detail.rows.map((row, rowIndex) => (
                      // Zeilen tragen keinen eigenen Schluessel im Beleg -- Index ist hier stabil,
                      // da `rows` nur einmal geladen und nicht umsortiert wird.
                      <Table.Tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <Table.Td key={cellIndex} className={numCols[cellIndex] ? classes.payColNum : undefined}>
                            {cell}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            </>
          )}
        </Drawer.Body>
        <div className={classes.drawerFooter}>
          <Button
            variant="default"
            loading={csvLoading}
            disabled={!detail}
            onClick={() => void handleCsvDownload()}
          >
            Gesendete Zeilen als CSV
          </Button>
          {detail?.canOpenEvaluation && (
            <Button color="dark" onClick={handleOpenEvaluation}>
              Zur Auswertung
            </Button>
          )}
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
}
